import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  ButtonLink,
  Column,
  FormBox,
  InputCnpj,
  InputText,
  Notice,
  PageActions,
  Row,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { isValidCNPJ } from "@helpers";
import { setActiveCompanyId } from "@helpers/activeCompany";
import { usePageHeader } from "@components/ow";
import { FormPage, focusFirstError } from "../../Common/FormPage";

// Nova empresa (modelo Formulário do DS OneWork), do drawer "Minhas Empresas". Só ADM
// (guard da rota): quem cria vira o administrador da empresa nova.

const BREADCRUMB = [{ page: "Nova empresa", route: "/c/companies/new" }];

const NAME_MAX = 255;

/** Erros por id do campo, na ordem da tela. */
export function validateNewCompany({ name, cnpj }) {
  const errors = {};
  if (name.trim().length < 2) errors["new-company-name"] = "Informe o nome da empresa.";
  if (!isValidCNPJ(cnpj)) errors["new-company-cnpj"] = "CNPJ inválido: confira os números.";
  return errors;
}

export default function NewCompany() {
  const companyService = useService("companies");
  const navigate = useNavigate();
  const [values, setValues] = useState({ name: "", cnpj: "" });
  const [loading, setLoading] = useState(false);
  // Os erros aparecem nos campos depois da primeira tentativa e se atualizam a cada mudança
  const [submitted, setSubmitted] = useState(false);

  usePageHeader({ title: "Nova empresa", breadcrumb: BREADCRUMB });

  const errors = submitted ? validateNewCompany(values) : {};
  const change = (field) => (e) => setValues((prev) => ({ ...prev, [field]: e.target.value }));

  async function handleSubmit() {
    setSubmitted(true);
    const found = validateNewCompany(values);
    if (Object.keys(found).length) {
      focusFirstError(Object.keys(found));
      return;
    }

    setLoading(true);
    const res = await companyService.createCompany({ name: values.name.trim(), cnpj: values.cnpj });

    if (res && !res.error && res.data?.companyId) {
      toast.success("Empresa criada! Entrando na nova empresa…");
      // Reload completo para menu, marca e dados trocarem juntos, como na troca pelo drawer
      setActiveCompanyId(res.data.companyId);
      setTimeout(() => {
        window.location.href = "/";
      }, 1200);
      return;
    }

    // A mensagem do back (CNPJ já cadastrado etc.) já saiu no toast do service
    setLoading(false);
  }

  return (
    <FormPage
      notice={
        <Notice
          $variant="blue"
          $text="Crie outra empresa para administrar com o mesmo login. Ela é totalmente independente desta: colaboradores, contratos, projetos e configurações ficam separados, e você será o administrador dela. Depois é só alternar entre as empresas em Minhas Empresas."
        />
      }
      actions={
        <PageActions>
          <ButtonLink $text="Cancelar" $action={() => navigate("/")} $isDisabled={loading} />
          <Button $text="Criar empresa" $variant="primary" $action={handleSubmit} $isLoading={loading} />
        </PageActions>
      }
    >
      <FormBox title="Dados da empresa">
        <Row $gap={20}>
          <Column $columns={6}>
            <InputText
              $idForm="new-company-name"
              $label="Nome da empresa"
              $variant="default"
              $errors={errors["new-company-name"]}
              maxLength={NAME_MAX}
              placeholder="Ex.: Onework LTDA"
              value={values.name}
              onChange={change("name")}
            />
          </Column>
          <Column $columns={3}>
            <InputCnpj
              $idForm="new-company-cnpj"
              $label="CNPJ"
              $variant="default"
              $errors={errors["new-company-cnpj"]}
              value={values.cnpj}
              onChange={change("cnpj")}
            />
          </Column>
        </Row>
      </FormBox>
    </FormPage>
  );
}
